import { logNonFatal } from '~/lib/diagnostics'
import { validRemoteImageUrl } from '~/lib/remote-image'
import { m } from '~/paraglide/messages'
import type { ItemType } from './bootstrap'
import type {
  CandidateContent,
  CatalogueContent,
  DetailSlide,
  DetailSlides,
  LandSkinArt,
} from './catalogue'
import type { DisplayMetadata, JSONValue } from './display-metadata'

// Land-skin art (GLOSSARY.md "Land-skin art"): the item type's top-level
// icon_image_url for the catalogue card and unlock-success surface, and the
// detail dialog's slides — display metadata's optional `detail_img_urls`
// list, or a lone slide of the icon when the list yields nothing.

// The title suffix each slide position carries (GLOSSARY.md "Detail slide"):
// the first slide showcases the forest, the second the home screen, the third
// a focus session; any later slide shows the bare title. Fixed by position
// rather than named in the payload, and spelled out entry by entry so every
// `m.*` access stays static for tree shaking — a dynamic `m[key]` would pull
// the whole namespace into the bundle.
const titleSuffixBySlide = new Map<number, () => string>([
  [0, m.shop_land_title_showcase_forest],
  [1, m.shop_land_title_showcase_home],
  [2, m.shop_land_title_showcase_focusing],
])

// null = the item type has no usable icon URL, and the product cannot render
// a land-skin card; the caller omits it under its own collection's event.
// The slides never omit: any way of lacking a usable list falls back to the
// icon, and only what is present but unusable is reported (the
// countdown_tag_color treatment).
export function deriveLandSkinArt(
  itemType: ItemType,
  displayMetadata: DisplayMetadata | null,
  productId: number,
  gid: number,
): LandSkinArt | null {
  const iconUrl = validRemoteImageUrl(itemType.icon_image_url)
  if (!iconUrl) return null

  const detailSlides = deriveDetailSlides(displayMetadata?.detail_img_urls, {
    product_id: String(productId),
    gid: String(gid),
  })
  return {
    detailSlides: detailSlides ?? [{ imageUrl: iconUrl, titleSuffix: '' }],
    iconUrl,
  }
}

// null = no slide to show; the caller falls back to the icon. An absent or
// null list is quiet. A list that is not one, or is empty, is reported once;
// an entry that is not a usable http(s) URL is dropped and reported by index,
// and the rest keep their order — so a list whose every entry is dropped
// explains itself entry by entry, with no second report for the fallback.
function deriveDetailSlides(
  raw: JSONValue | undefined,
  context: Record<string, string>,
): DetailSlides | null {
  if (raw === undefined || raw === null) return null
  if (!Array.isArray(raw) || raw.length === 0) {
    logNonFatal('shop_invalid_detail_image_ignored', context)
    return null
  }

  const slides: DetailSlide[] = []
  raw.forEach((entry, index) => {
    const imageUrl = validRemoteImageUrl(entry)
    if (!imageUrl) {
      logNonFatal('shop_invalid_detail_image_ignored', {
        ...context,
        index: String(index),
      })
      return
    }
    // The suffix follows the position in the list as sent, so a dropped
    // earlier entry does not hand its suffix to the next image.
    slides.push({
      imageUrl,
      titleSuffix: titleSuffixBySlide.get(index)?.() ?? '',
    })
  })
  const [first, ...rest] = slides
  return first ? [first, ...rest] : null
}

// The featured collections' shared derivation step: the content half of a
// candidate, spreadable straight into it. A tree is just its kind; a land
// skin is its kind plus its art. null = a land skin without a usable icon,
// which the caller omits under its own collection's event (the verdict is
// the caller's to report, like parsePurchaseEnd's).
export function deriveCandidateContent(
  content: CatalogueContent,
  itemType: ItemType,
  displayMetadata: DisplayMetadata | null,
  productId: number,
  gid: number,
): CandidateContent | null {
  if (content === 'tree_type') return { content }
  const landSkinArt = deriveLandSkinArt(
    itemType,
    displayMetadata,
    productId,
    gid,
  )
  return landSkinArt ? { content, landSkinArt } : null
}
